"use strict";
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.getEvaluator = void 0;
var CustomErrors_1 = require("./CustomErrors");
var java_regex_js_1 = require("java-regex-js");
var some = function (value) { return ({
    _tag: "some",
    isSome: true,
    value: value,
}); };
var none = {
    _tag: "none",
    isSome: false,
};
var isSome = function (maybe) {
    return maybe._tag === "some";
};
var isNone = function (maybe) {
    return maybe._tag === "none";
};
var maybeFromUndefined = function (value) {
    if (typeof value === "undefined") {
        return none;
    }
    return some(value);
};
var getEvaluator = function (rootContext, functionsAndVariables, options) {
    var _a, _b;
    var disableBoolOpChecks = (_a = options === null || options === void 0 ? void 0 : options.disableBoolOpChecks) !== null && _a !== void 0 ? _a : false;
    var disableNullPointerExceptions = (_b = options === null || options === void 0 ? void 0 : options.disableNullPointerExceptions) !== null && _b !== void 0 ? _b : false;
    var stack = [rootContext]; // <- could be a class.
    var getHead = function () {
        if (stack.length > 0) {
            return stack[stack.length - 1];
        }
        throw new CustomErrors_1.UnexpectedError("Stack is empty");
    };
    /**
     * see test for foo.identity(#this.a)
     * (we want the outer #this, not foo.a)
     * to do so, we need to keep track of #this, outside of our current head (onto which the property 'foo' has been pushed)
     */
    var ixOfThisBeforeCompoundOpened = (function () {
        var _ix = [];
        return {
            pushCurrent: function () { return _ix.push(stack.length - 1); },
            get: function () { var _a; return (_a = _ix[_ix.length - 1]) !== null && _a !== void 0 ? _a : null; },
            pop: function () { return _ix.pop(); },
            hasSome: function () { return _ix.length > 0; },
        };
    })();
    var getValueInProvidedFuncsAndVars = function (variableName) {
        if (variableName === "this") {
            return some(getHead());
        }
        else if (variableName === "root") {
            return some(stack[0]);
        }
        else {
            return maybeFromUndefined(functionsAndVariables[variableName]);
        }
    };
    var searchForPropertyValueInContextStack = function (variable) {
        return __spreadArray([], stack, true).reverse().reduce(function (prev, curr) {
            if (isSome(prev)) {
                return prev;
            }
            else if (variable === "this") {
                return some(curr);
            }
            else if (curr !== null && typeof curr !== "undefined") {
                return maybeFromUndefined(curr[variable]);
            }
            else {
                return none;
            }
        }, none);
    };
    function binFloatOp(op, allowNull) {
        if (allowNull === void 0) { allowNull = false; }
        return function (_left, _right) {
            var left = evaluate(_left);
            var right = evaluate(_right);
            if (typeof left !== "number" &&
                !(allowNull && (left === null || typeof left === "undefined"))) {
                throw new Error(JSON.stringify(left) + " is not a float");
            }
            if (typeof right !== "number" &&
                !(allowNull && (right === null || typeof right === "undefined"))) {
                throw new Error(JSON.stringify(right) + " is not a float");
            }
            return op((left !== null && left !== void 0 ? left : null), (right !== null && right !== void 0 ? right : null));
        };
    }
    var binStringOp = function (op) {
        return function (_left, _right) {
            var left = evaluate(_left);
            var right = evaluate(_right);
            if (typeof left !== "string") {
                throw new Error(JSON.stringify(left) + " is not a string");
            }
            if (typeof right !== "string") {
                throw new Error(JSON.stringify(right) + " is not a string");
            }
            return op(left, right);
        };
    };
    var find = function (array, expression, reverse) {
        var value = reverse ? array.reverse() : array;
        var result = value.find(function (e) {
            stack.push(e);
            var result = evaluate(expression);
            stack.pop();
            if (disableBoolOpChecks) {
                return Boolean(result);
            }
            if (typeof result !== "boolean") {
                throw new Error("Result of selection expression is not Boolean");
            }
            return result === true;
        });
        return typeof result === "undefined" ? null : result;
    };
    var evaluate = function (ast, isCompound, isFirstInCompound) {
        var _a, _b;
        var _c, _d;
        if (isCompound === void 0) { isCompound = false; }
        if (isFirstInCompound === void 0) { isFirstInCompound = false; }
        switch (ast.type) {
            case "BooleanLiteral":
                return ast.value;
            case "CompoundExpression": {
                ixOfThisBeforeCompoundOpened.pushCurrent();
                var res = ast.expressionComponents.reduce(function (_, curr, i) {
                    var isFirst = i === 0;
                    var res = evaluate(curr, true, isFirst);
                    stack.push(res);
                    return res;
                }, rootContext);
                ast.expressionComponents.forEach(function () {
                    stack.pop();
                });
                ixOfThisBeforeCompoundOpened.pop();
                return res;
            }
            case "Elvis": {
                var expr = evaluate(ast.expression);
                if (expr === null) {
                    return evaluate(ast.ifFalse);
                }
                else {
                    return expr;
                }
            }
            case "FunctionReference": {
                var maybeProvidedFunction = getValueInProvidedFuncsAndVars(ast.functionName);
                var evaluatedArguments = ast.args.map(function (arg) { return evaluate(arg); });
                if (isNone(maybeProvidedFunction)) {
                    if (!ast.nullSafeNavigation) {
                        throw new Error("Function " + ast.functionName + " not found.");
                    }
                    else {
                        return null;
                    }
                }
                var value = maybeProvidedFunction.value;
                if (typeof value !== "function") {
                    throw new Error("Variable " + ast.functionName + " is not a function.");
                }
                return value.apply(void 0, evaluatedArguments);
            }
            case "Indexer": {
                var head = getHead();
                if (head === null && ast.nullSafeNavigation) {
                    return null;
                }
                var index = evaluate(ast.index);
                if (typeof head === "string" && typeof index === "number") {
                    if (index >= 0 && index < head.length) {
                        return head[index];
                    }
                    throw new Error("index " +
                        index +
                        " is out of range on string " +
                        JSON.stringify(head));
                }
                else if (Array.isArray(head) && typeof index === "number") {
                    if (index >= 0 && index < head.length) {
                        return head[index];
                    }
                    throw new Error("index " +
                        index +
                        " is out of range on array " +
                        JSON.stringify(head));
                }
                else if (head &&
                    typeof head === "object" &&
                    (typeof index === "string" || typeof index === "number")) {
                    if (Object.prototype.hasOwnProperty.call(head, index)) {
                        return head[index];
                    }
                    else {
                        return null;
                    }
                }
                throw new Error("Not supported: indexing into " +
                    JSON.stringify(head) +
                    " with " +
                    JSON.stringify(index));
            }
            case "InlineList": {
                return ast.elements.map(function (el) { return evaluate(el); });
            }
            case "InlineMap": {
                return Object.entries(ast.elements).reduce(function (prev, _a) {
                    var k = _a[0], v = _a[1];
                    prev[k] = evaluate(v);
                    return prev;
                }, {});
            }
            case "MethodReference": {
                var evaluateArg_1 = function (arg) {
                    if (!ixOfThisBeforeCompoundOpened.hasSome()) {
                        return evaluate(arg);
                    }
                    // store the old stack, so we can put it back
                    var storedStack = stack;
                    // Now set a stack so #this points outside the head of our current compound expression
                    stack = stack.slice(0, ixOfThisBeforeCompoundOpened.get() + 1);
                    // evaluate with temporary stack
                    var result = evaluate(arg);
                    // put the stack back.
                    stack = storedStack;
                    return result;
                };
                if (ast.methodName === "length") {
                    var currentContext = getHead();
                    if (typeof currentContext === "string") {
                        return currentContext.length;
                    }
                }
                if (ast.methodName === "matches") {
                    var currentContext = getHead();
                    if (typeof currentContext === "string") {
                        var rx = evaluateArg_1(ast.args[0]);
                        if (typeof rx !== "string") {
                            throw new Error("Cannot call 'string.matches()' with argument of type " +
                                typeof rx);
                        }
                        return (0, java_regex_js_1.compile)(rx)(currentContext);
                    }
                }
                if (ast.methodName === "size") {
                    var currentContext = getHead();
                    if (Array.isArray(currentContext)) {
                        return currentContext.length;
                    }
                }
                if (ast.methodName === "contains") {
                    var currentContext = getHead();
                    if (Array.isArray(currentContext)) {
                        return currentContext.includes(evaluateArg_1(ast.args[0]));
                    }
                }
                var head = getHead();
                var valueInTopContext = head === null || head === void 0 ? void 0 : head[ast.methodName];
                if (valueInTopContext) {
                    var evaluatedArguments = ast.args.map(function (arg) { return evaluateArg_1(arg); }); // <- arguments are evaluated lazily
                    if (typeof valueInTopContext === "function") {
                        var boundFn = valueInTopContext.bind(head);
                        return boundFn.apply(void 0, evaluatedArguments);
                    }
                }
                else if (!ast.nullSafeNavigation) {
                    throw new Error("Method " + ast.methodName + " not found.");
                }
                return null;
            }
            case "Negative": {
                var operand = evaluate(ast.value);
                if (typeof operand === "number") {
                    return operand * -1;
                }
                throw new Error("unary (-) operator applied to " + JSON.stringify(operand));
            }
            case "NullLiteral": {
                return null;
            }
            case "NumberLiteral": {
                return ast.value;
            }
            case "OpAnd": {
                var left = evaluate(ast.left);
                if (!disableBoolOpChecks && typeof left !== "boolean") {
                    throw new Error(JSON.stringify(left) + " is not a boolean");
                }
                if (!left) {
                    return !!left;
                }
                var right = evaluate(ast.right);
                if (!disableBoolOpChecks && typeof right !== "boolean") {
                    throw new Error(JSON.stringify(right) + " is not a boolean");
                }
                return !!right;
            }
            case "OpDivide": {
                return binFloatOp(function (a, b) { return a / b; })(ast.left, ast.right);
            }
            case "OpEQ": {
                var left = evaluate(ast.left);
                var right = evaluate(ast.right);
                // boolean, number, string, null
                return left === right;
            }
            case "OpGE": {
                return binFloatOp(function (a, b) { return a >= b; }, true)(ast.left, ast.right);
            }
            case "OpGT": {
                return binFloatOp(function (a, b) { return a > b; }, true)(ast.left, ast.right);
            }
            case "OpLE": {
                return binFloatOp(function (a, b) { return a <= b; }, true)(ast.left, ast.right);
            }
            case "OpLT": {
                return binFloatOp(function (a, b) { return a < b; }, true)(ast.left, ast.right);
            }
            case "OpMatches": {
                return binStringOp(function (a, b) { return (0, java_regex_js_1.compile)(b)(a); })(ast.left, ast.right);
            }
            case "OpBetween": {
                var left = evaluate(ast.left);
                var right = evaluate(ast.right);
                if (!Array.isArray(right) || right.length !== 2) {
                    throw new Error("Right operand for the between operator has to be a two-element list");
                }
                var firstValue = right[0], secondValue = right[1];
                return firstValue <= left && left <= secondValue;
            }
            case "OpMinus": {
                return binFloatOp(function (a, b) { return a - b; })(ast.left, ast.right);
            }
            case "OpModulus": {
                return binFloatOp(function (a, b) { return a % b; })(ast.left, ast.right);
            }
            case "OpMultiply": {
                return binFloatOp(function (a, b) { return a * b; })(ast.left, ast.right);
            }
            case "OpNE": {
                var left = evaluate(ast.left);
                var right = evaluate(ast.right);
                // boolean, number, string, null
                return left !== right;
            }
            case "OpNot": {
                var exp = evaluate(ast.expression);
                return !exp;
            }
            case "OpOr": {
                var left = evaluate(ast.left);
                if (!disableBoolOpChecks && typeof left !== "boolean") {
                    throw new Error(JSON.stringify(left) + " is not a boolean");
                }
                if (left) {
                    return !!left;
                }
                var right = evaluate(ast.right);
                if (!disableBoolOpChecks && typeof right !== "boolean") {
                    throw new Error(JSON.stringify(right) + " is not a boolean");
                }
                return !!right;
            }
            case "OpPlus": {
                var left = (_c = evaluate(ast.left)) !== null && _c !== void 0 ? _c : null;
                var right = (_d = evaluate(ast.right)) !== null && _d !== void 0 ? _d : null;
                var isStringOrNumber = function (value) {
                    return (typeof value == "bigint" ||
                        typeof value === "string" ||
                        typeof value === "number" ||
                        value === null);
                };
                if (!isStringOrNumber(left)) {
                    throw new Error(JSON.stringify(left) + " is not a string or number.");
                }
                if (!isStringOrNumber(right)) {
                    throw new Error(JSON.stringify(right) + " is not a string or number.");
                }
                if (left === null && right === null) {
                    throw new Error("Operator + is not valid between operands null and null");
                }
                if (left == null && typeof right === "number") {
                    throw new Error("Operator + is not valid between operands null and of type number");
                }
                if (typeof left == "number" && right === null) {
                    throw new Error("Operator + is not valid between operands of type number and null");
                }
                // any is because typescript is being unreasonable here
                return left + right;
            }
            case "OpPower": {
                return binFloatOp(function (a, b) { return Math.pow(a, b); })(ast.base, ast.expression);
            }
            case "Projection": {
                var nullSafeNavigation = ast.nullSafeNavigation, expression_1 = ast.expression;
                var head = getHead();
                if (head === null && nullSafeNavigation) {
                    return null;
                }
                if (Array.isArray(head)) {
                    return head.map(function (v) {
                        stack.push(v);
                        var result = evaluate(expression_1);
                        stack.pop();
                        return result;
                    });
                }
                if (head && typeof head === "object") {
                    return Object.values(head).map(function (v) {
                        stack.push(v);
                        var result = evaluate(expression_1);
                        stack.pop();
                        return result;
                    });
                }
                throw new Error("Cannot run expression on non-array " + JSON.stringify(head));
            }
            case "PropertyReference": {
                var nullSafeNavigation = ast.nullSafeNavigation, propertyName = ast.propertyName;
                if (isCompound && !isFirstInCompound) {
                    // we can only get the head.
                    var head = getHead();
                    if (typeof head[propertyName] === "undefined") {
                        if (nullSafeNavigation) {
                            return null;
                        }
                        if (disableNullPointerExceptions) {
                            return null;
                        }
                        throw new Error("Null Pointer Exception: Property " +
                            JSON.stringify(propertyName) +
                            " not found in head (last position)" +
                            " of context " +
                            JSON.stringify(stack));
                    }
                    return head[propertyName];
                }
                var valueInContext = searchForPropertyValueInContextStack(propertyName);
                if (isNone(valueInContext)) {
                    if (nullSafeNavigation) {
                        return null;
                    }
                    if (disableNullPointerExceptions) {
                        return null;
                    }
                    throw new Error("Null Pointer Exception: Property " +
                        JSON.stringify(propertyName) +
                        " not found in context " +
                        JSON.stringify(stack));
                }
                return valueInContext.value;
            }
            case "SelectionAll": {
                var nullSafeNavigation = ast.nullSafeNavigation, expression_2 = ast.expression;
                var head = getHead();
                if (head === null && nullSafeNavigation) {
                    return null;
                }
                if (Array.isArray(head)) {
                    return head.filter(function (v, i) {
                        stack.push(v);
                        var result = evaluate(expression_2);
                        stack.pop();
                        if (disableBoolOpChecks) {
                            return Boolean(result);
                        }
                        if (typeof result !== "boolean") {
                            throw new Error("Result " +
                                JSON.stringify(result) +
                                " at index " +
                                i +
                                " of selection expression is not Boolean");
                        }
                        return result === true;
                    });
                }
                if (head && typeof head === "object") {
                    // pojo
                    return Object.fromEntries(Object.entries(head).filter(function (_a, i) {
                        var key = _a[0], value = _a[1];
                        stack.push({ key: key, value: value });
                        var result = evaluate(expression_2);
                        stack.pop();
                        if (disableBoolOpChecks) {
                            return Boolean(result);
                        }
                        if (typeof result !== "boolean") {
                            throw new Error("Result " +
                                JSON.stringify(result) +
                                " at index " +
                                i +
                                " of selection expression is not Boolean");
                        }
                        return result === true;
                    }));
                }
                throw new Error("Cannot run selection expression on non-collection " +
                    JSON.stringify(head));
            }
            case "SelectionFirst": {
                var nullSafeNavigation = ast.nullSafeNavigation, expression = ast.expression;
                var head = getHead();
                if (head === null && nullSafeNavigation) {
                    return null;
                }
                if (Array.isArray(head)) {
                    return find(head, expression, false);
                }
                if (head && typeof head === "object") {
                    var result = find(Object.entries(head).map(function (_a) {
                        var key = _a[0], value = _a[1];
                        return ({ key: key, value: value });
                    }), expression, false);
                    return result && (_a = {}, _a[result.key] = result.value, _a);
                }
                throw new Error("Cannot run selection expression on non-array " + JSON.stringify(head));
            }
            case "SelectionLast": {
                var nullSafeNavigation = ast.nullSafeNavigation, expression = ast.expression;
                var head = getHead();
                if (head === null && nullSafeNavigation) {
                    return null;
                }
                if (Array.isArray(head)) {
                    return find(head, expression, true);
                }
                if (head && typeof head === "object") {
                    var result = find(Object.entries(head).map(function (_a) {
                        var key = _a[0], value = _a[1];
                        return ({ key: key, value: value });
                    }), expression, true);
                    return result && (_b = {}, _b[result.key] = result.value, _b);
                }
                throw new Error("Cannot run selection expression on non-array " + JSON.stringify(head));
            }
            case "StringLiteral": {
                return ast.value;
            }
            case "Ternary": {
                var expression = ast.expression, ifTrue = ast.ifTrue, ifFalse = ast.ifFalse;
                var conditionResult = evaluate(expression);
                if (conditionResult === true ||
                    (disableBoolOpChecks && Boolean(conditionResult))) {
                    return evaluate(ifTrue);
                }
                else if (conditionResult === false || conditionResult === null) {
                    return evaluate(ifFalse);
                }
                else {
                    throw new Error("Unexpected non boolean/null in Ternary conditional expression: " +
                        JSON.stringify(conditionResult));
                }
            }
            case "VariableReference": {
                var valueInFuncsAndVars = getValueInProvidedFuncsAndVars(ast.variableName);
                if (isNone(valueInFuncsAndVars)) {
                    if (disableNullPointerExceptions) {
                        return null;
                    }
                    throw new Error("Null Pointer Exception: variable " +
                        JSON.stringify(ast.variableName) +
                        " not found");
                }
                return valueInFuncsAndVars.value;
            }
        }
    };
    return function (ast) { return evaluate(ast); };
};
exports.getEvaluator = getEvaluator;
//# sourceMappingURL=Evaluate.js.map